/**
 * Shared dietary form options + helpers for dancers and guests.
 * Keep field names aligned so meal exports stay consistent:
 *   diet, dietDetails, gluten, foodsNotConsumed, foodAllergies, dietaryNotes
 */

export const DIETARY_OPTIONS = [
  {
    key: 'standard',
    short: 'Standard',
    description: 'Meat, fish, poultry, eggs, dairy — the whole spread.'
  },
  {
    key: 'vegetarian',
    short: 'Vegetarian',
    description: 'No meat or fish. Eggs and dairy might still be fine.'
  },
  {
    key: 'vegan',
    short: 'Vegan',
    description: 'Plants only — no animal products.'
  },
  {
    key: 'pescetarian',
    short: 'Pescetarian',
    description: 'Fish is fine; skip red meat and poultry.'
  }
]

/** Yes/no food chips by diet style (vegan has none). */
export const DETAIL_BY_DIET = {
  standard: ['Red meat', 'Chicken', 'Pork', 'Eggs', 'Dairy'],
  vegetarian: ['Eggs', 'Dairy'],
  pescetarian: ['Eggs', 'Dairy'],
  vegan: []
}

export const GLUTEN_OPTIONS = [
  { key: 'ok', label: 'Yes, gluten is fine' },
  { key: 'prefer-none', label: 'I’d rather skip it' },
  { key: 'celiac', label: 'Celiac / very sensitive' }
]

/** In-room coffee maker stock (onsite / roommate). Shared by dietary + lodging. */
export const COFFEE_MAKER_NONE = 'None'
export const COFFEE_MAKER_GROUPS = [
  {
    label: 'To drink',
    options: ['Coffee', 'Decaf coffee', 'Black tea', 'Green tea']
  },
  {
    label: 'Creamy stuff',
    options: ['Cream', 'Almond milk', 'Oat milk']
  },
  {
    label: 'A little sweet',
    options: ['Sugar', 'Splenda', 'Stevia']
  },
  {
    label: 'Nothing for me',
    options: [COFFEE_MAKER_NONE]
  }
]

export function hasCoffeeMakerPicks (stock, coffee) {
  if (Array.isArray(stock) && stock.length) {
    return true
  }
  return typeof coffee === 'string' && coffee.trim().length > 0
}

/** Drinks (coffee first, tea last), then creamy stuff, then sweeteners. */
export function orderedCoffeeMakerStock (stock) {
  const set = new Set(Array.isArray(stock) ? stock : [])
  const ordered = []
  COFFEE_MAKER_GROUPS.forEach((group) => {
    group.options.forEach((opt) => {
      if (opt !== COFFEE_MAKER_NONE && set.has(opt)) {
        ordered.push(opt)
      }
    })
  })
  set.forEach((item) => {
    if (item && item !== COFFEE_MAKER_NONE && ordered.indexOf(item) === -1) {
      ordered.push(item)
    }
  })
  if (!ordered.length && set.has(COFFEE_MAKER_NONE)) {
    return [COFFEE_MAKER_NONE]
  }
  return ordered
}

export function coffeeMakerSummary (stock, coffee) {
  if (Array.isArray(stock) && stock.length) {
    if (stock.length === 1 && stock[0] === COFFEE_MAKER_NONE) {
      return 'Nothing extra — you don’t need the coffee maker stocked.'
    }
    return 'We’ll leave this in your room: ' + orderedCoffeeMakerStock(stock).join(', ')
  }
  if (typeof coffee === 'string' && coffee.trim()) {
    return 'We’ll leave this in your room: ' + coffee.trim()
  }
  return ''
}

export function toggleExclusiveNone (current, option, exclusiveNone) {
  const none = exclusiveNone || COFFEE_MAKER_NONE
  const list = Array.isArray(current) ? current : []
  if (option === none) {
    return list.includes(none) ? [] : [none]
  }
  let next = list.filter(x => x !== none)
  if (next.includes(option)) {
    next = next.filter(x => x !== option)
  } else {
    next = next.concat([option])
  }
  return next
}

export const FOOD_ALLERGIES_OPTIONS = [
  'None',
  'Peanuts / peanut butter',
  'Tree nuts',
  'Shellfish',
  'Dairy / lactose',
  'Soy',
  'Eggs',
  'Tomatoes',
  'Chocolate',
  'Mushrooms',
  'Something else (add a note)'
]

/** Legacy strings that used to live in foodAllergies / foodsNotConsumed */
export const LEGACY_GLUTEN_CELIAC = [
  'Gluten — celiac',
  'Gluten - celiac',
  'Gluten',
  'No Gluten - Celiac',
  'No Gluten — Celiac'
]
export const LEGACY_GLUTEN_PREFER = [
  'Gluten — preference',
  'Gluten - preference',
  'No Gluten - Preference',
  'No Gluten — Preference'
]
export const LEGACY_GLUTEN_ANY = LEGACY_GLUTEN_CELIAC.concat(LEGACY_GLUTEN_PREFER)

export function emptyDietaryFormState () {
  return {
    diet: '',
    dietDetails: [],
    dietDetailAnswers: {},
    gluten: '',
    foodAllergies: [],
    dietaryNotes: ''
  }
}

export function dietDetailOptionsFor (diet) {
  return DETAIL_BY_DIET[diet] || []
}

export function dietOptionMeta (dietKey) {
  return DIETARY_OPTIONS.find(o => o.key === dietKey) || null
}

export function glutenLabel (glutenKey) {
  const match = GLUTEN_OPTIONS.find(o => o.key === glutenKey)
  return match ? match.label : (glutenKey || '—')
}

/**
 * Legacy admin field: avoided items from step 2 + gluten status.
 * Same shape as dancer dietaryRestrictions.
 */
export function buildFoodsNotConsumed (diet, dietDetails, gluten) {
  const options = DETAIL_BY_DIET[diet] || []
  const happyWith = Array.isArray(dietDetails) ? dietDetails : []
  let avoided = []
  if (!options.length) {
    avoided = diet === 'vegan' ? ['No animal products'] : []
  } else {
    avoided = options
      .filter(o => !happyWith.includes(o))
      .map(o => 'No ' + o)
  }
  if (gluten === 'prefer-none') {
    avoided = avoided.concat(['No Gluten - Preference'])
  } else if (gluten === 'celiac') {
    avoided = avoided.concat(['No Gluten - Celiac'])
  }
  return avoided
}

/**
 * @param {object} state dietary form state
 * @returns {string|null} error message or null if valid
 */
export function validateDietaryForm (state) {
  if (!state || !state.diet) {
    return 'Pick how they usually eat so we know where to start.'
  }
  const detailOpts = dietDetailOptionsFor(state.diet)
  if (detailOpts.length) {
    const answers = state.dietDetailAnswers || {}
    const unanswered = detailOpts.filter(
      o => answers[o] !== true && answers[o] !== false
    )
    if (unanswered.length) {
      return 'A quick yes or no for each food above, please!'
    }
  }
  if (!state.gluten) {
    return 'Tell us about gluten — yes, skip it, or very sensitive.'
  }
  if (!state.foodAllergies || !state.foodAllergies.length) {
    return 'Tap any allergies we should watch for — or None if they’re good.'
  }
  return null
}

/**
 * Build the Firestore payload fields shared by dancers and guests.
 * Also sets dietaryRestriction for older guest rows/exports.
 */
export function dietaryPayloadFromForm (state) {
  const diet = state.diet || ''
  const detailOpts = dietDetailOptionsFor(diet)
  const answers = state.dietDetailAnswers || {}
  const dietDetails = detailOpts.length
    ? detailOpts.filter(o => answers[o] === true)
    : (Array.isArray(state.dietDetails) ? state.dietDetails : [])
  const gluten = state.gluten || ''
  const foodAllergies = Array.isArray(state.foodAllergies) ? state.foodAllergies : []
  const dietaryNotes = state.dietaryNotes || ''
  return {
    diet,
    dietDetails,
    gluten,
    foodsNotConsumed: buildFoodsNotConsumed(diet, dietDetails, gluten),
    foodAllergies,
    dietaryNotes,
    // Legacy guest field — same value as diet for older admin UI / exports
    dietaryRestriction: diet,
    // Legacy free-text allergies bucket (prefer structured foodAllergies + notes)
    otherAllergies: foodAllergies.filter(a => a !== 'None').join(', ') || ''
  }
}

/**
 * Human-readable diet line for summaries / tables.
 * Supports new fields and legacy guest dietaryRestriction.
 */
export function dietaryDisplayLine (record) {
  if (!record) {
    return ''
  }
  const dietKey = record.diet || record.dietaryRestriction || ''
  const meta = dietOptionMeta(dietKey)
  if (meta) {
    return meta.short
  }
  return dietKey || ''
}

/**
 * Allergies display for tables/exports.
 */
export function allergiesDisplayLine (record) {
  if (!record) {
    return ''
  }
  if (Array.isArray(record.foodAllergies) && record.foodAllergies.length) {
    const list = record.foodAllergies.filter(a => a && a !== 'None')
    if (list.length) {
      return list.join(', ')
    }
    if (record.foodAllergies.includes('None')) {
      return 'None'
    }
  }
  return record.otherAllergies || ''
}

export function glutenDisplayLine (record) {
  if (!record) {
    return ''
  }
  if (record.gluten) {
    return glutenLabel(record.gluten)
  }
  return ''
}
